import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import useSeo from "../hooks/useSeo";
import { SiteFooter } from "../components/SiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stripe Checkout redirects here with ?session_id=... after a successful
// payment. We poll the backend (which talks to Stripe) until the session
// flips to "paid", then push the user into /dashboard.
//
// Polling cadence: every 1.5s, max 20 attempts (~30s) — Stripe usually
// reports paid within 2-3s. On timeout or 404 we surface a recovery state.
const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  // "polling" | "paid" | "expired" | "error" | "missing"
  const [state, setState] = useState(sessionId ? "polling" : "missing");
  const [planId, setPlanId] = useState(null);
  const attemptsRef = useRef(0);
  useSeo({ title: "Confirming payment — Bilateral Calm", path: "/payment-success", noindex: true });

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const res = await axios.get(
          `${API}/billing/checkout-status/${sessionId}`,
          { withCredentials: true }
        );
        if (cancelled) return;
        const status = res.data?.status;
        if (status === "paid") {
          setPlanId(res.data?.plan_id || null);
          setState("paid");
          // Send them to the dashboard after a brief celebration moment so
          // the success state isn't a blink-and-miss.
          setTimeout(() => navigate("/dashboard", { replace: true }), 2200);
          return;
        }
        if (status === "expired") {
          setState("expired");
          return;
        }
        if (attemptsRef.current >= 20) {
          setState("error");
          return;
        }
        setTimeout(poll, 1500);
      } catch (err) {
        if (cancelled) return;
        // 404 means the transaction doesn't belong to this user — usually
        // an expired session cookie. Push them to sign-in to recover.
        if (err.response?.status === 404 || err.response?.status === 401) {
          setState("missing");
          return;
        }
        if (attemptsRef.current >= 20) {
          setState("error");
          return;
        }
        setTimeout(poll, 1500);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, navigate]);

  return (
    <div
      className="min-h-screen bg-[#FBF8F1] flex items-center justify-center px-6 py-12"
      data-testid="payment-success-page"
    >
      <div className="w-full max-w-md text-center">
        <button
          onClick={() => navigate("/")}
          className="mx-auto mb-8 flex items-center justify-center w-full"
          aria-label="Bilateral Calm home"
        >
          <img
            src="/logo-bilateral-calm.png"
            alt="Bilateral Calm"
            className="h-28 sm:h-32 w-auto mx-auto"
          />
        </button>

        <div className="bg-white/90 backdrop-blur-sm rounded-3xl border border-slate-100 shadow-[0_14px_44px_rgba(0,0,0,0.06)] p-8 sm:p-10">
          {state === "polling" && (
            <div data-testid="payment-polling">
              <Loader2 className="w-12 h-12 text-[#3090A8] animate-spin mx-auto mb-4" />
              <h1 className="font-outfit font-semibold text-2xl text-[#1B2349] mb-2">
                Confirming your payment…
              </h1>
              <p className="text-[#4A5568] leading-relaxed">
                Stripe is finishing up. This usually takes just a few seconds —
                please don't close this window.
              </p>
            </div>
          )}

          {state === "paid" && (
            <div data-testid="payment-paid">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#E7F4EA] flex items-center justify-center mb-4">
                <CheckCircle2 className="w-10 h-10 text-[#3FA363]" />
              </div>
              <h1 className="font-outfit font-semibold text-2xl text-[#1B2349] mb-2">
                You're in. Welcome.
              </h1>
              <p className="text-[#4A5568] leading-relaxed mb-6">
                {planId === "monthly"
                  ? "Your monthly subscription is active. The full library is unlocked."
                  : planId === "annual"
                  ? "Your annual subscription is active. The full library is unlocked."
                  : "Your subscription is active. The full library is unlocked."}
              </p>
              <Button
                onClick={() => navigate("/dashboard", { replace: true })}
                className="w-full justify-center rounded-full bg-[#1B2349] text-white font-medium py-3 hover:bg-[#0F1838]"
                data-testid="payment-success-dashboard-btn"
              >
                Go to your dashboard
              </Button>
            </div>
          )}

          {state === "expired" && (
            <div data-testid="payment-expired">
              <XCircle className="w-12 h-12 text-[#D98880] mx-auto mb-4" />
              <h1 className="font-outfit font-semibold text-2xl text-[#1B2349] mb-2">
                Checkout session expired
              </h1>
              <p className="text-[#4A5568] leading-relaxed mb-6">
                The payment window closed before it could complete. No charge
                was made.
              </p>
              <Button
                onClick={() => navigate("/pricing")}
                className="w-full justify-center rounded-full bg-[#1B2349] text-white font-medium py-3 hover:bg-[#0F1838]"
                data-testid="payment-expired-retry-btn"
              >
                Try again
              </Button>
            </div>
          )}

          {state === "error" && (
            <div data-testid="payment-error">
              <XCircle className="w-12 h-12 text-[#D98880] mx-auto mb-4" />
              <h1 className="font-outfit font-semibold text-2xl text-[#1B2349] mb-2">
                Still confirming…
              </h1>
              <p className="text-[#4A5568] leading-relaxed mb-6">
                We couldn't confirm your payment automatically. If your card
                was charged, your subscription will activate shortly — open
                your dashboard to check, or contact support if it doesn't
                update within a few minutes.
              </p>
              <Button
                onClick={() => navigate("/dashboard", { replace: true })}
                className="w-full justify-center rounded-full bg-[#1B2349] text-white font-medium py-3 hover:bg-[#0F1838]"
                data-testid="payment-error-dashboard-btn"
              >
                Open dashboard
              </Button>
            </div>
          )}

          {state === "missing" && (
            <div data-testid="payment-missing">
              <XCircle className="w-12 h-12 text-[#D98880] mx-auto mb-4" />
              <h1 className="font-outfit font-semibold text-2xl text-[#1B2349] mb-2">
                We couldn't find this checkout
              </h1>
              <p className="text-[#4A5568] leading-relaxed mb-6">
                You may have been signed out, or the link is incomplete. Sign
                in to see your subscription status — if your card was charged,
                your subscription will be active.
              </p>
              <Button
                onClick={() => navigate("/sign-in")}
                className="w-full justify-center rounded-full bg-[#1B2349] text-white font-medium py-3 hover:bg-[#0F1838]"
                data-testid="payment-missing-signin-btn"
              >
                Sign in
              </Button>
            </div>
          )}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
};

export default PaymentSuccessPage;
